import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import { getPrintQRListAction } from "../../../store/actions/printQRActions";

const PrintQR = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get data from Redux store
  const qrList = useSelector((state) => state.printQR?.printQRList || []);
  const loading = useSelector((state) => state.printQR?.loading || false);
  
  // Search and pagination states
  const [searchQueries, setSearchQueries] = useState({
    medicine_name: "",
    batch_number: "",
  });
  const [sortField, setSortField] = useState("medicine_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const [error, setError] = useState("");
  const rowsPerPage = 10;
  
  // Add debounce timer ref
  const searchTimeoutRef = useRef(null);

  // Fetch QR list on component mount
  useEffect(() => {
    const fetchQRList = async () => {
      setError("");
      try {
        await dispatch(getPrintQRListAction());
      } catch (err) {
        console.error("Failed to fetch QR list:", err);
        setError("Failed to load QR records. Please try again.");
      }
    };
    fetchQRList();
  }, [dispatch]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearch = (field, value) => {
    const clearedQueries = {
      medicine_name: "",
      batch_number: "",
    };
    
    const updatedQueries = { ...clearedQueries, [field]: value };
    setSearchQueries(updatedQueries);
    setCurrentPage(0);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce the search to avoid too many operations
    searchTimeoutRef.current = setTimeout(() => {
      // Search is handled by local filtering
    }, 300);
  };
  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };
  
  // Handle print - open qrcodePDF URL in new tab
  const handlePrint = (qr) => {
    if (qr.carton_qrcode && qr.carton_qrcode!== "") {
      window.open(qr.carton_qrcode, "_blank");
    } else {
      swal("Info", "No PDF available for this QR code", "info");
    }
  };

  // Filter and sort data
  const filteredData = qrList
    .filter((qr) =>
      Object.keys(searchQueries).every((key) => {
        const searchValue = searchQueries[key].toLowerCase();
        let itemValue = "";
        
        if (key === "medicine_name") {
          itemValue = (qr.medicine_name || "").toString().toLowerCase();
        } else if (key === "batch_number") {
          itemValue = (qr.batch_no || "").toString().toLowerCase();
        } else {
          itemValue = (qr[key] || "").toString().toLowerCase();
        }
        
        return searchValue === "" || itemValue.includes(searchValue);
      })
    )
    .sort((a, b) => {
      const modifier = sortOrder === "asc" ? 1 : -1;
      let aVal, bVal;
      
      if (sortField === "medicine_name") {
        aVal = (a.medicine_name || "").toString().toLowerCase();
        bVal = (b.medicine_name || "").toString().toLowerCase();
      } else if (sortField === "batch_number") {
        aVal = (a.batch_no || "").toString().toLowerCase();
        bVal = (b.batch_no || "").toString().toLowerCase();
      } else {
        aVal = a[sortField];
        bVal = b[sortField];
      }
      
      if (aVal < bVal) return -1 * modifier;
      if (aVal > bVal) return 1 * modifier;
      return 0;
    });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };
  
  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link to="/app-create-batch-QR" className="btn btn-primary btn-rounded">
            + Add Batch
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {error && <div className="alert alert-danger mb-3">{error}</div>}
          
          {loading ? (
            <div className="text-center my-5">
              <Loader size="lg" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped mb-4">
                  <thead className="table-light">
                    <tr>
                      <th onClick={() => handleSort("medicine_name")} style={{ cursor: "pointer" }}>
                        Medicine Name {sortField === "medicine_name" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          placeholder="Search medicine"
                          value={searchQueries.medicine_name}
                          onChange={(e) => handleSearch("medicine_name", e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th onClick={() => handleSort("batch_number")} style={{ cursor: "pointer" }}>
                        Batch Number {sortField === "batch_number" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          placeholder="Search batch"
                          value={searchQueries.batch_number}
                          onChange={(e) => handleSearch("batch_number", e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                  
                      <th>Cartons</th>
                      <th>Quantity</th> 
                    <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((item, index) => (
                      <tr key={item.id || index}>
                        <td>{item.medicine_name}</td>
                        <td>{item.batch_no}</td>
                    
                        <td>{item.no_of_carton || "-"}</td>
                        <td>{item.qty|| "-"}</td>
                       <td className="text-center">
  <button
    className="btn btn-link p-0"
    onClick={() => handlePrint(item)}
  >
    <i className="fa fa-print text-info fs-18"></i>
  </button>
</td>
                      </tr>
                    ))}
                    {paginatedData.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-4">
                          No QR records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              {filteredData.length > 0 && (
                <div
                  className="pagination-wrapper"
                  style={{
                    display: "flex",
                    gap: "6px",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "20px",
                    flexWrap: "wrap",
                    flexDirection: "column",
                  }}
                >
                  <div>
                    Showing {paginatedData?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                    {Math.min((currentPage + 1) * rowsPerPage, filteredData.length)} of {filteredData.length} entries
                  </div>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                    <button
                      onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                      disabled={currentPage === 0}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        backgroundColor: "#fff",
                        color: "#000",
                        cursor: currentPage === 0 ? "not-allowed" : "pointer",
                        opacity: currentPage === 0 ? 0.5 : 1,
                      }}
                    >
                      &lt;
                    </button>
                    {(() => {
                      const pages = [];
                      const maxPagesToShow = 5;
                      const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow));
                      const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                      if (startPage > 0) {
                        pages.push(
                          <button
                            key={0}
                            onClick={() => handlePageChange(0)}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              backgroundColor: "#fff",
                              color: "#000",
                              cursor: "pointer",
                            }}
                          >
                            1
                          </button>
                        );
                        if (startPage > 1) {
                          pages.push(
                            <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                              ...
                            </span>
                          );
                        }
                      }

                      for (let i = startPage; i < endPage; i++) {
                        const isActive = currentPage === i;
                        pages.push(
                          <button
                            key={i}
                            onClick={() => handlePageChange(i)}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              backgroundColor: isActive ? "#007bff" : "#fff",
                              color: isActive ? "#fff" : "#000",
                              cursor: "pointer",
                            }}
                          >
                            {i + 1}
                          </button>
                        );
                      }

                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <button
                            key={totalPages - 1}
                            onClick={() => handlePageChange(totalPages - 1)}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              backgroundColor: "#fff",
                              color: "#000",
                              cursor: "pointer",
                            }}
                          >
                            {totalPages}
                          </button>
                        );
                      }

                      return pages;
                    })()}
                    <button
                      onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                      disabled={currentPage === totalPages - 1}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        backgroundColor: "#fff",
                        color: "#000",
                        cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                        opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                      }}
                    >
                      &gt;
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PrintQR;